/**
 * Background Sync Engine
 *
 * Processes offline queue when network reconnects
 * - FIFO processing
 * - Stops on first failure
 * - Retry logic with backoff
 * - Conflict resolution
 */

import {
  getPendingActions,
  updateActionStatus,
  removeAction,
  shouldRetry,
  QueuedAction,
  // QueueActionType,
} from "./offlineQueue";
import { driverPublicAPI, driversAPI } from "../services/api";
import { store } from "../store";
import { setOnline } from "../store/slices/networkSlice";

interface SyncProgress {
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  current: QueuedAction | null;
}

type SyncListener = (progress: SyncProgress) => void;

class SyncEngine {
  private isProcessing = false;
  private listeners: SyncListener[] = [];
  private currentProgress: SyncProgress = {
    total: 0,
    processed: 0,
    succeeded: 0,
    failed: 0,
    current: null,
  };

  /**
   * Add sync progress listener
   */
  addListener(listener: SyncListener) {
    this.listeners.push(listener);
  }

  /**
   * Remove sync progress listener
   */
  removeListener(listener: SyncListener) {
    this.listeners = this.listeners.filter((l) => l !== listener);
  }

  /**
   * Notify all listeners of progress
   */
  private notifyListeners() {
    this.listeners.forEach((listener) => listener(this.currentProgress));
  }

  /**
   * Process a single action
   */
  private async processAction(action: QueuedAction): Promise<boolean> {
    // console.log("[Sync] Processing action:", action.type, action.id);

    try {
      await updateActionStatus(action.id, "processing");

      switch (action.type) {
        case "STATUS_UPDATE":
          await this.processStatusUpdate(action);
          break;

        case "ADD_COMMENT":
          await this.processAddComment(action);
          break;

        case "DELETE_COMMENT":
          await this.processDeleteComment(action);
          break;

        case "ADD_SERVICE":
          await this.processAddService(action);
          break;

        case "UPLOAD_ATTACHMENT":
          await this.processUploadAttachment(action);
          break;

        case "DELETE_ATTACHMENT":
          await this.processDeleteAttachment(action);
          break;

        default:
          throw new Error(`Unknown action type: ${action.type}`);
      }

      // Success - mark as success and remove
      await updateActionStatus(action.id, "success");
      await removeAction(action.id);

      // console.log("[Sync] Action succeeded:", action.type, action.id);
      return true;
    } catch (error: any) {
      console.error("[Sync] Action failed:", action.type, action.id, error);

      // Check if we should retry
      if (shouldRetry(action)) {
        // Mark as pending for retry
        await updateActionStatus(action.id, "pending", error.message);
        // console.log("[Sync] Will retry action:", action.id);
      } else {
        // Max retries reached, mark as failed
        await updateActionStatus(action.id, "failed", error.message);
        console.error("[Sync] Action failed permanently:", action.id);
      }

      return false;
    }
  }

  /**
   * Process status update action
   */
  private async processStatusUpdate(action: QueuedAction): Promise<void> {
    const { token, status, latitude, longitude, isProtected } = action.payload;
    const api = isProtected ? driversAPI : driverPublicAPI;

    switch (status) {
      case "ReadyForPickup":
        await api.setReadyForPickup(token, latitude, longitude);
        break;
      case "AwaitingPickup":
        await api.setAwaitingPickup(token, latitude, longitude);
        break;
      case "InTransit":
        await api.startJourney(token, latitude, longitude);
        break;
      case "Completed":
        await api.completeJourney(token, latitude, longitude);
        break;
      default:
        throw new Error(`Unknown status: ${status}`);
    }
  }

  /**
   * Process add comment action
   */
  private async processAddComment(action: QueuedAction): Promise<void> {
    const { token, commentData, isProtected } = action.payload;
    const api = isProtected ? driversAPI : driverPublicAPI;

    await api.addShipmentComment(token, commentData);
  }

  /**
   * Process delete comment action
   */
  private async processDeleteComment(action: QueuedAction): Promise<void> {
    const { token, commentId, isProtected } = action.payload;
    const api = isProtected ? driversAPI : driverPublicAPI;

    await api.deleteShipmentComment(token, commentId);
  }

  /**
   * Process add service action
   */
  private async processAddService(action: QueuedAction): Promise<void> {
    const { token, services, isProtected } = action.payload;
    const api = isProtected ? driversAPI : driverPublicAPI;

    await api.addService(token, services);
  }

  /**
   * Process upload attachment action
   */
  private async processUploadAttachment(action: QueuedAction): Promise<void> {
    const { token, isProtected } = action.payload;
    const api = isProtected ? driversAPI : driverPublicAPI;

    // Reconstruct FormData from stored data
    const reconstructedFormData = new FormData();
    for (const [key, value] of Object.entries(action.payload.formDataEntries)) {
      if (value instanceof File) {
        reconstructedFormData.append(key, value);
      } else {
        reconstructedFormData.append(key, value as string);
      }
    }

    await api.uploadAttachments(token, reconstructedFormData);
  }

  /**
   * Process delete attachment action
   */
  private async processDeleteAttachment(action: QueuedAction): Promise<void> {
    const { token, attachmentId, isProtected } = action.payload;
    const api = isProtected ? driversAPI : driverPublicAPI;

    await api.deleteAttachment(token, attachmentId);
  }

  /**
   * Start sync process
   */
  async startSync(): Promise<void> {
    if (this.isProcessing) {
      // console.log("[Sync] Already processing, skipping");
      return;
    }

    if (!navigator.onLine) {
      // console.log("[Sync] Offline, skipping sync");
      return;
    }

    try {
      this.isProcessing = true;
      // console.log("[Sync] Starting sync process...");

      // Get all pending actions
      const actions = await getPendingActions();

      if (actions.length === 0) {
        // console.log("[Sync] No pending actions");
        this.isProcessing = false;
        return;
      }

      // console.log("[Sync] Processing", actions.length, "actions");

      // Initialize progress
      this.currentProgress = {
        total: actions.length,
        processed: 0,
        succeeded: 0,
        failed: 0,
        current: null,
      };
      this.notifyListeners();

      // Process actions in FIFO order
      for (const action of actions) {
        this.currentProgress.current = action;
        this.notifyListeners();

        const success = await this.processAction(action);

        this.currentProgress.processed++;
        if (success) {
          this.currentProgress.succeeded++;
        } else {
          this.currentProgress.failed++;

          // Stop on first failure (as per requirements)
          // console.log("[Sync] Stopping sync due to failure");
          break;
        }

        this.notifyListeners();
      }

      this.currentProgress.current = null;
      this.notifyListeners();

      // console.log("[Sync] Sync complete:", {
      //   processed: this.currentProgress.processed,
      //   succeeded: this.currentProgress.succeeded,
      //   failed: this.currentProgress.failed,
      // });
    } catch (error) {
      console.error("[Sync] Sync process error:", error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Get current sync progress
   */
  getProgress(): SyncProgress {
    return { ...this.currentProgress };
  }

  /**
   * Check if currently syncing
   */
  isSyncing(): boolean {
    return this.isProcessing;
  }
}

// Singleton instance
export const syncEngine = new SyncEngine();

/**
 * Initialize sync engine listeners
 * Call this in your App.tsx
 */
export const initializeSyncEngine = () => {
  // Listen for online event
  window.addEventListener("online", () => {
    // console.log("[Sync] Network online, starting sync...");
    store.dispatch(setOnline());

    // Start sync after a short delay to ensure network is stable
    setTimeout(() => {
      syncEngine.startSync().catch((err) => {
        console.error("[Sync] Auto-sync failed:", err);
      });
    }, 1000);
  });

  // Also attempt an immediate sync on init in case
  // there are already pending actions and we're online.
  if (navigator.onLine) {
    // console.log("[Sync] Initialized while online, checking for pending actions...");
    setTimeout(() => {
      syncEngine.startSync().catch((err) => {
        console.error("[Sync] Initial sync failed:", err);
      });
    }, 1000);
  }

  // console.log("[Sync] Sync engine initialized");
};

/**
 * Manually trigger sync
 */
export const manualSync = async (): Promise<void> => {
  if (!navigator.onLine) {
    throw new Error("Cannot sync while offline");
  }

  await syncEngine.startSync();
};
